import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SearchBox from "./SearchBox";
import { pages } from "./pages";
import { appAction, refreshMessages, updateHomeMessage } from "./helpers";
import { useEffect, useState } from "react";

const messageTypes = ['info', 'success', 'warning', 'error'];
const messageTypeAliases = {
    alert: 'warning',
}

const normalizeMessageType = (type) => {
    const normalized = messageTypeAliases[type] ?? type;
    return messageTypes.includes(normalized) ? normalized : 'info';
}

const HomePage = ({ appState, dispatch }) => {
    const isAdmin = appState.user.admin === true;
    const homeLinks = Object.values(pages).filter(page => page.shortcut && (!page.admin || isAdmin))
    const message = (appState.messages ?? [])[0] ?? { type: 'info', text: '' };
    const [messageText, setMessageText] = useState(message.text);
    const [messageType, setMessageType] = useState(message.type ?? 'info');
    const [editingMessage, setEditingMessage] = useState(false);
    const [savingMessage, setSavingMessage] = useState(false);

    useEffect(() => {
        refreshMessages(dispatch).catch(() => {})
    }, [dispatch])

    useEffect(() => {
        setMessageText(message.text)
        setMessageType(normalizeMessageType(message.type))
    }, [message.text, message.type])

    const openMessageEditor = () => {
        setMessageText(message.text)
        setMessageType(normalizeMessageType(message.type))
        setEditingMessage(true)
    }

    const saveMessage = (text = messageText, type = messageType) => {
        setSavingMessage(true)
        updateHomeMessage({ text, type }, dispatch)
            .then(() => {
                setEditingMessage(false)
            })
            .catch((error) => {
                window.alert(error.message || 'Sorry, something went wrong!')
            })
            .finally(() => {
                setSavingMessage(false)
            })
    }

    return (
        <Stack direction="column" spacing={2}>
            {(appState.messages ?? []).map((message, i) => (
                <Alert
                    severity={normalizeMessageType(message.type)}
                    key={i}
                    action={isAdmin &&
                        <IconButton
                            color="inherit"
                            size="small"
                            onClick={openMessageEditor}
                        >
                            <EditIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    {message.text}
                </Alert>
            ))}
            {isAdmin && !message.text &&
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" onClick={openMessageEditor}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Box>
            }
            <img src="/plexbot.svg" height="64px" alt="Plexbot" />
            <SearchBox appState={appState} dispatch={dispatch} />
            <div style={{ display: 'flex', flexDirection: 'row' }} >
                {homeLinks.map(page => (
                    <div
                        key={page.id}
                        style={{ flex: 1, textAlign: 'center' }}
                    >
                        <div
                            style={{ cursor: 'pointer', display: 'inline-block', paddingInline: '10px' }}
                            onClick={() => {
                                dispatch({ type: appAction.update, value: { currentPage: page.id } })
                            }}
                        >
                            {page.icon}
                        </div>
                    </div>
                ))}
            </div>
            <Dialog open={editingMessage} onClose={() => setEditingMessage(false)} fullWidth maxWidth="sm">
                <DialogTitle>Home Message</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <ToggleButtonGroup
                            value={messageType}
                            exclusive
                            size="small"
                            onChange={(event, value) => {
                                if (value) {
                                    setMessageType(value)
                                }
                            }}
                        >
                            {messageTypes.map(type => (
                                <ToggleButton key={type} value={type}>
                                    {type}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <TextField
                            label="Home message"
                            multiline
                            minRows={3}
                            value={messageText}
                            onChange={(event) => setMessageText(event.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        startIcon={<ClearIcon />}
                        disabled={savingMessage || !messageText}
                        onClick={() => saveMessage('', messageType)}
                    >
                        Clear
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={savingMessage}
                        onClick={() => saveMessage()}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}


export default HomePage;
